import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  UsersResponseSchema,
  SetUserPolicyResponseSchema,
} from "@/api/schemas";

// Roster of the tenant's end-users (X-User-Id values) plus the tenant's
// effective tier ceiling. Powers both the per-user filter dropdown and the
// Governance per-user table.
export function useUsers(tenantId: string) {
  return useQuery({
    queryKey: ["users", tenantId],
    queryFn: async () => {
      const json = await api.get("users").json();
      return UsersResponseSchema.parse(json);
    },
    enabled: !!tenantId,
  });
}

export function useSetUserTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId: _tenantId,
      externalUserId,
      dataTier,
    }: {
      tenantId: string;
      externalUserId: string;
      dataTier: "privacy" | "collaborative";
    }) => {
      const json = await api
        .post(`users/${encodeURIComponent(externalUserId)}/tier`, {
          json: { data_tier: dataTier },
        })
        .json();
      return SetUserPolicyResponseSchema.parse(json);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users", variables.tenantId] });
    },
  });
}

export function useSetUserBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId: _tenantId,
      externalUserId,
      monthlyBudgetUsd,
    }: {
      tenantId: string;
      externalUserId: string;
      monthlyBudgetUsd: number | null;
    }) => {
      const json = await api
        .post(`users/${encodeURIComponent(externalUserId)}/budget`, {
          json: { monthly_budget_usd: monthlyBudgetUsd },
        })
        .json();
      return SetUserPolicyResponseSchema.parse(json);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users", variables.tenantId] });
    },
  });
}
